import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import i18n from "src/i18n";
import { z } from "zod";

import { useUpdateSlotTemplate } from "@hooks/mutations/slotTemplate/useUpdateSlotTemplate";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useAuth } from "@contexts/auth";

import { DailySchedule, DailyTimeBlock, SlotTemplate } from "@customTypes/slotTemplate";

import { TimeBlockManager } from "../TimeBlockManager";

import { StyledEditSlotTemplateModalContent } from "./EditSlotTemplateModal.styled";


const editFormSchema = () =>
  z
    .object({
      name: z.string().min(1, i18n.t("template-name-required")),
      description: z.string().optional(),
      doctorAssigned: z.string().min(1, i18n.t("doctor-required")),
      recurrenceType: z.enum(["daily", "weekly", "specific_days"]),
      daysOfWeek: z.array(z.number()).optional(),
      startDate: z.date({
        required_error: i18n.t("start-date-required"),
        invalid_type_error: i18n.t("start-date-required"),
      }),
      endDate: z.date().nullable().optional(),
      hasEndDate: z.boolean(),
      quantity: z.number().min(1, i18n.t("quantity-required")),
      bookingLeadTimeMinutes: z.number().min(0, i18n.t("booking-lead-time-required")),
      isActive: z.boolean(),
    })
    .refine(
      data => {
        if (data.recurrenceType === "weekly" || data.recurrenceType === "specific_days") {
          return data.daysOfWeek && data.daysOfWeek.length > 0;
        }
        return true;
      },
      {
        message: i18n.t("days-of-week-required"),
        path: ["daysOfWeek"],
      }
    );

type EditFormInputs = z.infer<ReturnType<typeof editFormSchema>>;

interface EditSlotTemplateModalProps {
  opened: boolean;
  close: () => void;
  template: SlotTemplate | null;
}

export const EditSlotTemplateModal = ({
  opened,
  close,
  template,
}: EditSlotTemplateModalProps) => {
  const { t } = useTranslation();
  const { fullName, roles } = useAuth();
  const isDoctorView = roles.includes("doctor") && !roles.includes("admin");

  const [dailySchedule, setDailySchedule] = useState<DailySchedule>({});

  const { mutate: updateTemplate, isLoading: isUpdateLoading } =
    useUpdateSlotTemplate();

  const { data: doctorsData } = useGetUsers({ role: "doctor", isEnabled: !isDoctorView });

  const doctors = useMemo(
    () =>
      doctorsData?.map(doctor => ({
        label: doctor.fullName,
        value: doctor.id,
      })) || [],
    [doctorsData]
  );

  const form = useForm<EditFormInputs>({
    validate: zodResolver(editFormSchema()),
    initialValues: {
      name: "",
      description: "",
      doctorAssigned: "",
      recurrenceType: "weekly",
      daysOfWeek: [],
      startDate: new Date(),
      endDate: null,
      hasEndDate: false,
      quantity: 1,
      bookingLeadTimeMinutes: 0,
      isActive: true,
    },
  });

  useEffect(() => {
    if (template && opened) {
      form.setValues({
        name: template.name,
        description: template.description || "",
        doctorAssigned: template.doctorAssigned,
        recurrenceType: template.recurrence.type,
        daysOfWeek: template.recurrence.daysOfWeek || [],
        startDate: new Date(template.recurrence.startDate),
        endDate: template.recurrence.endDate ? new Date(template.recurrence.endDate) : null,
        hasEndDate: !!template.recurrence.endDate,
        quantity: template.slotConfig.quantity,
        bookingLeadTimeMinutes: template.slotConfig.bookingLeadTimeMinutes || 0,
        isActive: template.isActive,
      });

      // Set daily schedule
      setDailySchedule(template.dailySchedule);
    }
  }, [template, opened]);

  // Auto-populate time blocks for newly selected days
  useEffect(() => {
    if (!template) return;

    const selectedDays = form.values.daysOfWeek || [];
    const newSchedule: DailySchedule = { ...dailySchedule };

    // Add default time block for newly selected days
    selectedDays.forEach(day => {
      if (!newSchedule[day] || newSchedule[day].length === 0) {
        newSchedule[day] = [
          {
            startTime: "09:00",
            endTime: "17:00",
            slotDurationMinutes: 30,
            bufferTimeMinutes: 5,
          },
        ];
      }
    });

    // Remove time blocks for unselected days
    Object.keys(newSchedule).forEach(dayStr => {
      const day = Number(dayStr);
      if (!selectedDays.includes(day)) {
        delete newSchedule[day];
      }
    });

    setDailySchedule(newSchedule);
  }, [form.values.daysOfWeek]);

  const handleSubmit = (values: EditFormInputs) => {
    if (!template) return;

    // Convert dailySchedule to API format
    const dailyScheduleApi: Record<number, any[]> = {};
    Object.entries(dailySchedule).forEach(([day, blocks]) => {
      dailyScheduleApi[Number(day)] = blocks.map((block: DailyTimeBlock) => ({
        start_time: block.startTime,
        end_time: block.endTime,
        slot_duration_minutes: block.slotDurationMinutes,
        buffer_time_minutes: block.bufferTimeMinutes,
      }));
    });

    updateTemplate(
      {
        templateId: template.id,
        data: {
          name: values.name,
          description: values.description,
          daily_schedule: dailyScheduleApi,
          recurrence: {
            type: values.recurrenceType,
            days_of_week:
              values.recurrenceType !== "daily" ? values.daysOfWeek : undefined,
            start_date: values.startDate.toISOString().split("T")[0],
            end_date: values.hasEndDate && values.endDate
              ? values.endDate.toISOString().split("T")[0]
              : null,
          },
          slot_config: {
            quantity: values.quantity,
            default_booked: false,
            booking_lead_time_minutes: values.bookingLeadTimeMinutes,
          },
          is_active: values.isActive,
        },
      },
      {
        onSuccess: () => {
          close();
        },
      }
    );
  };

  const daysOfWeekOptions = [
    { value: 1, label: t("mon-short") },
    { value: 2, label: t("tue-short") },
    { value: 3, label: t("wed-short") },
    { value: 4, label: t("thu-short") },
    { value: 5, label: t("fri-short") },
    { value: 6, label: t("sat-short") },
    { value: 0, label: t("sun-short") },
  ];

  const toggleDay = (day: number) => {
    const currentDays = form.values.daysOfWeek || [];
    if (currentDays.includes(day)) {
      form.setFieldValue(
        "daysOfWeek",
        currentDays.filter(d => d !== day)
      );
    } else {
      form.setFieldValue("daysOfWeek", [...currentDays, day]);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={t("edit-template")}
      size="xl"
      styles={{
        body: {
          maxWidth: "100%",
          overflowX: "hidden",
        },
      }}>
      <StyledEditSlotTemplateModalContent>
        <ScrollAreaAutosize mah="70vh">
          <form className="form" onSubmit={form.onSubmit(handleSubmit)}>
            {/* Basic Information Section */}
            <div className="form-section">
              <div className="section-title">{t("basic-information")}</div>

              <TextInput
                className="form-field"
                label={t("template-name")}
                placeholder={t("template-name")}
                withAsterisk
                {...form.getInputProps("name")}
              />

              <Textarea
                className="form-field"
                label={t("template-description")}
                placeholder={t("template-description")}
                rows={3}
                {...form.getInputProps("description")}
              />

              {isDoctorView ? <Text>{t("doctor")}: {fullName}</Text> : <Select
                className="form-field"
                label={t("doctor")}
                placeholder={t("select-doctor")}
                data={doctors}
                searchable
                withAsterisk
                disabled
                {...form.getInputProps("doctorAssigned")}
              />}
            </div>

            {/* Recurrence Section */}
            <div className="form-section">
              <div className="section-title">{t("recurrence-pattern")}</div>

              <Select
                className="form-field"
                label={t("recurrence-type")}
                data={[
                  { value: "daily", label: t("daily") },
                  { value: "weekly", label: t("weekly") },
                  { value: "specific_days", label: t("specific-days") },
                ]}
                withAsterisk
                {...form.getInputProps("recurrenceType")}
              />

              {(form.values.recurrenceType === "weekly" ||
                form.values.recurrenceType === "specific_days") && (
                <div className="form-field">
                  <Text size="sm" fw={500} mb={8}>
                    {t("days-of-week")} <span style={{ color: "red" }}>*</span>
                  </Text>
                  <div className="checkbox-group">
                    {daysOfWeekOptions.map(day => (
                      <Checkbox
                        key={day.value}
                        label={day.label}
                        checked={form.values.daysOfWeek?.includes(day.value) || false}
                        onChange={() => toggleDay(day.value)}
                      />
                    ))}
                  </div>
                  {form.errors.daysOfWeek && (
                    <Text size="xs" c="red" mt={4}>
                      {form.errors.daysOfWeek}
                    </Text>
                  )}
                </div>
              )}

              <DateInput
                className="form-field"
                label={t("start-date")}
                minDate={new Date()}
                withAsterisk
                {...form.getInputProps("startDate")}
              />

              <Switch
                className="form-field"
                label={t("set-end-date")}
                {...form.getInputProps("hasEndDate", { type: "checkbox" })}
              />

              {form.values.hasEndDate && (
                <DateInput
                  className="form-field"
                  label={t("end-date")}
                  minDate={form.values.startDate}
                  {...form.getInputProps("endDate")}
                />
              )}
            </div>

            {/* Time Blocks Section */}
            <div className="form-section">
              <div className="section-title">{t("time-blocks")}</div>

              <TimeBlockManager
                selectedDays={form.values.daysOfWeek || []}
                dailySchedule={dailySchedule}
                onChange={setDailySchedule}
              />
            </div>

            {/* Slot Configuration Section */}
            <div className="form-section">
              <div className="section-title">{t("slot-configuration")}</div>

              <NumberInput
                className="form-field"
                label={t("quantity-per-slot")}
                description={t("appointments-per-time-slot")}
                min={1}
                max={20}
                withAsterisk
                {...form.getInputProps("quantity")}
              />

              <NumberInput
                className="form-field"
                label={t("booking-lead-time")}
                description={t("minimum-minutes-in-advance-to-book")}
                min={0}
                {...form.getInputProps("bookingLeadTimeMinutes")}
              />

              <Switch
                className="form-field"
                label={t("template-active")}
                description={t("toggle-template-active-status")}
                {...form.getInputProps("isActive", { type: "checkbox" })}
              />
            </div>

            <div className="form-actions">
              <PermissionGate permission="appointments.slots"><Button
                type="submit"
                loading={isUpdateLoading}
                disabled={isUpdateLoading}>
                {t("submit")}
              </Button></PermissionGate>
            </div>
          </form>
        </ScrollAreaAutosize>
      </StyledEditSlotTemplateModalContent>
    </Modal>
  );
};
